import { useState } from 'react'
import { Button, Drawer, SearchSelect, toast, type ComboboxItem } from '@/components/ui'
import { useCreatePipeline, useOpenPOsForPicker } from '../api/queries'

export interface NewQuoteDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated?: (pipelineId: number) => void
}

/**
 * Logistics overhaul gap fix — the "self-sufficient command center" entry
 * point for starting a new freight pipeline without leaving this page.
 * Mirrors `sample-requests/components/NewSampleRequestDrawer.tsx`'s shape:
 * a Drawer (not Modal, per the house Drawer > Modal rule) with a picker,
 * Cancel/Create footer, and an `onCreated` callback the page wires to
 * navigation.
 */
export function NewQuoteDrawer({ open, onOpenChange, onCreated }: NewQuoteDrawerProps) {
  const { data: options, isLoading } = useOpenPOsForPicker()
  const create = useCreatePipeline()

  const [selected, setSelected] = useState<{ po_id: number; po_number: string } | null>(null)

  function reset() {
    setSelected(null)
  }

  // cmdk's client filter matches `value` + `keywords`, NOT the rendered
  // `label` — without keywords, typing the PO number or vendor wouldn't
  // actually find anything (see SearchSelect.test.tsx's harness for the
  // same convention).
  const items: ComboboxItem[] = (options ?? []).map((po) => ({
    value: String(po.po_id),
    label: po.po_number,
    meta: po.vendor,
    keywords: [po.po_number, po.vendor],
  }))

  function submit() {
    if (!selected) return
    create.mutate(
      { odoo_po_id: selected.po_id, odoo_po_name: selected.po_number, requested_provider: null },
      {
        onSuccess: (row) => {
          toast.success(`Started a freight pipeline for ${selected.po_number}`)
          reset()
          onOpenChange(false)
          onCreated?.(row.id)
        },
        onError: (err) =>
          toast.danger(
            `Couldn't start a pipeline${err instanceof Error ? `: ${err.message}` : ''}`,
          ),
      },
    )
  }

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => {
        if (!next) reset()
        onOpenChange(next)
      }}
      title="New quote"
      meta="Start a freight pipeline for an open PO — quote and tender it from the work view that opens next."
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => {
              reset()
              onOpenChange(false)
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={create.isPending}
            disabled={!selected}
            onClick={submit}
          >
            Create
          </Button>
        </div>
      }
    >
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-secondary">Purchase order</span>
        <SearchSelect
          items={items}
          value={selected ? String(selected.po_id) : undefined}
          selectedLabel={selected?.po_number ?? null}
          onQueryChange={() => {}}
          onSelect={(val) => {
            const po = (options ?? []).find((p) => String(p.po_id) === val)
            if (po) setSelected({ po_id: po.po_id, po_number: po.po_number })
          }}
          onClear={() => setSelected(null)}
          placeholder={isLoading ? 'Loading open POs…' : 'Search an open PO…'}
          emptyMessage={isLoading ? 'Loading…' : 'No open POs found'}
          shouldFilter
          disabled={isLoading}
        />
        <span className="text-role-metadata">
          Sourced from open (unreceived) POs. Need a closed PO tracked? Ask an admin —
          that path isn&apos;t built yet.
        </span>
      </label>
    </Drawer>
  )
}
